import React, { useState, useEffect } from 'react'
import { backendService, DoctorSummary, TokenTransaction } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import { useColumnResize } from './useColumnResize'
import './DoctorList.css'

type SortField = 'name' | 'username' | 'balance' | 'created_date'
type SortOrder = 'asc' | 'desc'

export const DoctorList: React.FC = () => {
  const [doctors, setDoctors] = useState<DoctorSummary[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [limit] = useState(20)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [activeOnly, setActiveOnly] = useState(false)
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorSummary | null>(null)

  // Column resize
  const {
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    getColumnStyle,
  } = useColumnResize()

  // Global mouse listeners for resize
  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)
    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [handleResizeMove, handleResizeEnd])

  // Modal state for View Full Profile
  const [profileModalDoctor, setProfileModalDoctor] = useState<DoctorSummary | null>(null)

  // Modal state for Manage Balance
  const [balanceModalDoctor, setBalanceModalDoctor] = useState<DoctorSummary | null>(null)
  const [balanceAmount, setBalanceAmount] = useState('')
  const [balanceDescription, setBalanceDescription] = useState('')
  const [balanceTokenType, setBalanceTokenType] = useState<'tokens' | 'ai_tokens'>('tokens')
  const [isSubmittingBalance, setIsSubmittingBalance] = useState(false)
  const [balanceSubmitError, setBalanceSubmitError] = useState<string | null>(null)
  const [balanceSubmitSuccess, setBalanceSubmitSuccess] = useState<string | null>(null)

  // Modal state for View Activity
  const [activityModalDoctor, setActivityModalDoctor] = useState<DoctorSummary | null>(null)
  const [transactions, setTransactions] = useState<TokenTransaction[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)
  const [activityPage, setActivityPage] = useState(1)
  const [activityTotalPages, setActivityTotalPages] = useState(1)

  // Modal state for Delete Account
  const [deleteModalDoctor, setDeleteModalDoctor] = useState<DoctorSummary | null>(null)
  const [deletePassword, setDeletePassword] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Load doctors on mount and when filters or refresh trigger change
  useEffect(() => {
    loadDoctors()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy, sortOrder, activeOnly, refreshTrigger])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadDoctors()
      } else {
        setPage(1) // Reset to page 1 when searching
      }
    }, 500)

    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const loadDoctors = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await backendService.listAllDoctors(
        page,
        limit,
        searchTerm || undefined,
        sortBy,
        sortOrder,
        activeOnly
      )
      
      setDoctors(response.doctors)
      setTotalCount(response.totalCount)
      setTotalPages(response.totalPages)
      
      logInfo(`Loaded ${response.doctors.length} doctors (page ${page}/${response.totalPages})`, 'DoctorList')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load doctors'
      logError(`Failed to load doctors: ${errorMessage}`, 'DoctorList')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  const handleDoctorClick = (doctor: DoctorSummary) => {
    setSelectedDoctor(selectedDoctor?.id === doctor.id ? null : doctor)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return '⇅'
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  // ── View Full Profile ──
  const handleViewProfile = (doctor: DoctorSummary) => {
    setProfileModalDoctor(doctor)
  }

  const handleCloseProfile = () => {
    setProfileModalDoctor(null)
  }

  // ── Manage Balance ──
  const handleOpenBalance = (doctor: DoctorSummary) => {
    setBalanceModalDoctor(doctor)
    setBalanceAmount('')
    setBalanceDescription('')
    setBalanceSubmitError(null)
    setBalanceSubmitSuccess(null)
  }

  const handleCloseBalance = () => {
    setBalanceModalDoctor(null)
    setBalanceAmount('')
    setBalanceDescription('')
    setBalanceSubmitError(null)
    setBalanceSubmitSuccess(null)
  }

  const handleSubmitBalance = async () => {
    if (!balanceModalDoctor) return

    const amount = parseInt(balanceAmount, 10)
    if (isNaN(amount) || amount <= 0) {
      setBalanceSubmitError('Enter a valid positive number')
      return
    }
    if (!balanceDescription.trim()) {
      setBalanceSubmitError('Enter a description')
      return
    }

    setIsSubmittingBalance(true)
    setBalanceSubmitError(null)
    setBalanceSubmitSuccess(null)

    try {
      if (balanceTokenType === 'ai_tokens') {
        const result = await backendService.creditDoctorAiTokens(
          balanceModalDoctor.id,
          amount,
          balanceDescription.trim()
        )
        setBalanceSubmitSuccess(`Added ${amount} AI tokens. New balance: ${result.balance}`)
      } else {
        const result = await backendService.addDoctorTokens(
          balanceModalDoctor.id,
          { amount, description: balanceDescription.trim(), transactionType: 'credit' }
        )
        setBalanceSubmitSuccess(`Added ${amount} tokens. New balance: ${result.newBalance}`)
      }
      loadDoctors()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add tokens'
      setBalanceSubmitError(msg)
    } finally {
      setIsSubmittingBalance(false)
    }
  }

  // ── Delete Account ──
  const handleOpenDelete = (doctor: DoctorSummary) => {
    setDeleteModalDoctor(doctor)
    setDeletePassword('')
    setDeleteError(null)
  }

  const handleCloseDelete = () => {
    setDeleteModalDoctor(null)
    setDeletePassword('')
    setDeleteError(null)
  }

  const handleDeleteAccount = async () => {
    if (!deleteModalDoctor) return
    if (!deletePassword.trim()) {
      setDeleteError('Enter your admin password to confirm')
      return
    }

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await backendService.deleteDoctorAccount(deleteModalDoctor.id, deletePassword)
      logInfo(`Deleted doctor account: ${deleteModalDoctor.name}`, 'DoctorList')
      handleCloseDelete()
      setPage(1)
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete account'
      setDeleteError(msg)
    } finally {
      setIsDeleting(false)
    }
  }

  // ── View Activity ──
  const handleOpenActivity = async (doctor: DoctorSummary) => {
    setActivityModalDoctor(doctor)
    setActivityPage(1)
    setActivityTotalPages(1)
    setIsLoadingActivity(true)
    setTransactions([])

    try {
      const response = await backendService.getDoctorTokenHistory(doctor.id, 1, 20)
      setTransactions(response.transactions)
      setActivityPage(response.page)
      setActivityTotalPages(response.totalPages)
    } catch (err) {
      logError(`Failed to load activity: ${err instanceof Error ? err.message : err}`, 'DoctorList')
    } finally {
      setIsLoadingActivity(false)
    }
  }

  const handleCloseActivity = () => {
    setActivityModalDoctor(null)
    setTransactions([])
    setActivityPage(1)
    setActivityTotalPages(1)
  }

  const handleActivityPageChange = async (newPage: number) => {
    if (!activityModalDoctor || newPage < 1 || newPage > activityTotalPages) return

    setIsLoadingActivity(true)
    try {
      const response = await backendService.getDoctorTokenHistory(activityModalDoctor.id, newPage, 20)
      setTransactions(response.transactions)
      setActivityPage(response.page)
      setActivityTotalPages(response.totalPages)
    } catch (err) {
      logError(`Failed to load activity page: ${err instanceof Error ? err.message : err}`, 'DoctorList')
    } finally {
      setIsLoadingActivity(false)
    }
  }

  return (
    <div className="doctor-list" role="region" aria-label="Doctor List Management">
      {/* Header with Controls */}
      <div className="list-header">
        <div className="list-title-section">
          <h2 className="list-title" id="doctor-list-title">All Doctors</h2>
          <span className="list-count" aria-live="polite">{totalCount} total</span>
        </div>

        <div className="list-controls">
          {/* Search */}
          <div className="search-control">
            <label htmlFor="doctor-search" className="sr-only">Search doctors</label>
            <input
              id="doctor-search"
              type="text"
              className="search-input"
              placeholder="Search by name, username, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search doctors by name, username, or email"
            />
          </div>

          {/* Filter */}
          <div className="filter-control">
            <label className="filter-checkbox">
              <input
                id="active-only-filter"
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                aria-label="Show active doctors only"
              />
              <span>Active only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error" role="alert" aria-live="assertive">
          <span className="alert-icon" aria-hidden="true">❌</span>
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true"></span>
          <span>Loading doctors...</span>
        </div>
      )}

      {/* Doctor Table */}
      {!isLoading && doctors.length === 0 ? (
        <div className="empty-state" role="status">
          <div className="empty-icon" aria-hidden="true">👥</div>
          <h3>No Doctors Found</h3>
          <p>
            {searchTerm
              ? `No doctors match "${searchTerm}"`
              : 'No doctors registered yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="doctor-table" aria-labelledby="doctor-list-title">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('name')}
                    aria-sort={sortBy === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('name')}
                  >
                    Name {getSortIcon('name')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('name', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('username')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('username')}
                    aria-sort={sortBy === 'username' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('username')}
                  >
                    Username {getSortIcon('username')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('username', e)} />
                  </th>
                  <th style={getColumnStyle('email')}>
                    Email
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('email', e)} />
                  </th>
                  <th style={getColumnStyle('specialization')}>
                    Specialization
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('specialization', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('balance')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('balance')}
                    aria-sort={sortBy === 'balance' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('balance')}
                  >
                    Balance {getSortIcon('balance')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('balance', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('created_date')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('created_date')}
                    aria-sort={sortBy === 'created_date' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('created_date')}
                  >
                    Joined {getSortIcon('created_date')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('created_date', e)} />
                  </th>
                  <th style={getColumnStyle('status')}>
                    Status
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('status', e)} />
                  </th>
                  <th style={getColumnStyle('actions')}>
                    Actions
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('actions', e)} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {doctors.map(doctor => (
                  <React.Fragment key={doctor.id}>
                    <tr
                      className={`doctor-row ${selectedDoctor?.id === doctor.id ? 'expanded' : ''}`}
                      onClick={() => handleDoctorClick(doctor)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleDoctorClick(doctor)}
                      aria-expanded={selectedDoctor?.id === doctor.id}
                      aria-label={`Doctor ${doctor.name}, ${doctor.username}, balance ${doctor.balance} tokens`}
                    >
                      <td className="name-cell">
                        <div className="doctor-name">{doctor.name}</div>
                      </td>
                      <td className="username-cell">@{doctor.username}</td>
                      <td className="email-cell">{doctor.email}</td>
                      <td className="specialization-cell">
                        {doctor.specialization || '—'}
                      </td>
                      <td className="balance-cell">
                        <span className="balance-badge" aria-label={`${doctor.balance} tokens`}>{doctor.balance}</span>
                      </td>
                      <td className="date-cell">{formatDate(doctor.createdDate)}</td>
                      <td className="status-cell">
                        <span
                          className={`status-badge ${doctor.isActive ? 'active' : 'inactive'}`}
                          aria-label={doctor.isActive ? 'Active status' : 'Inactive status'}
                        >
                          {doctor.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDoctorClick(doctor)
                          }}
                          aria-label={selectedDoctor?.id === doctor.id ? `Hide details for ${doctor.name}` : `Show details for ${doctor.name}`}
                        >
                          {selectedDoctor?.id === doctor.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {selectedDoctor?.id === doctor.id && (
                      <tr className="details-row">
                        <td colSpan={8}>
                          <div className="doctor-details" role="region" aria-label={`Details for ${doctor.name}`}>
                            <div className="details-grid">
                              <div className="detail-item">
                                <span className="detail-label">Doctor ID:</span>
                                <span className="detail-value">{doctor.id}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Total Earned:</span>
                                <span className="detail-value">{doctor.totalEarned} tokens</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Total Spent:</span>
                                <span className="detail-value">{doctor.totalSpent} tokens</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Last Login:</span>
                                <span className="detail-value">
                                  {doctor.lastLogin ? formatDate(doctor.lastLogin) : 'Never'}
                                </span>
                              </div>
                            </div>
                            <div className="details-actions">
                              <button
                                className="btn btn-secondary"
                                onClick={(e) => { e.stopPropagation(); handleViewProfile(doctor) }}
                                aria-label={`View full profile for ${doctor.name}`}
                              >
                                View Full Profile
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={(e) => { e.stopPropagation(); handleOpenBalance(doctor) }}
                                aria-label={`Manage balance for ${doctor.name}`}
                              >
                                Manage Balance
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={(e) => { e.stopPropagation(); handleOpenActivity(doctor) }}
                                aria-label={`View activity for ${doctor.name}`}
                              >
                                View Activity
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={(e) => { e.stopPropagation(); handleOpenDelete(doctor) }}
                                aria-label={`Delete account for ${doctor.name}`}
                                style={{ marginLeft: 'auto' }}
                              >
                                Delete Account
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="pagination" role="navigation" aria-label="Doctor list pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                aria-label="Go to first page"
              >
                ⟨⟨ First
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                aria-label="Go to previous page"
              >
                ⟨ Previous
              </button>

              <div className="pagination-pages" role="group" aria-label="Page numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (page <= 3) {
                    pageNum = i + 1
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = page - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={page === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                aria-label="Go to next page"
              >
                Next ⟩
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
                aria-label="Go to last page"
              >
                Last ⟩⟩
              </button>

              <span className="pagination-info" aria-live="polite" aria-atomic="true">
                Page {page} of {totalPages} ({totalCount} total)
              </span>
            </nav>
          )}
        </>
      )}

      {/* ── View Full Profile Modal ── */}
      {profileModalDoctor && (
        <div className="modal-overlay" onClick={handleCloseProfile} role="dialog" aria-modal="true" aria-label={`Full profile for ${profileModalDoctor.name}`}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{profileModalDoctor.name}</h2>
              <button className="modal-close" onClick={handleCloseProfile} aria-label="Close profile">&times;</button>
            </div>
            <div className="modal-body">
              <div className="profile-section">
                <div className="profile-row"><span className="profile-label">ID:</span><span className="profile-value">{profileModalDoctor.id}</span></div>
                <div className="profile-row"><span className="profile-label">Username:</span><span className="profile-value">@{profileModalDoctor.username}</span></div>
                <div className="profile-row"><span className="profile-label">Email:</span><span className="profile-value">{profileModalDoctor.email}</span></div>
                <div className="profile-row"><span className="profile-label">Specialization:</span><span className="profile-value">{profileModalDoctor.specialization || '—'}</span></div>
                <div className="profile-row"><span className="profile-label">Status:</span><span className={`profile-badge ${profileModalDoctor.isActive ? 'active' : 'inactive'}`}>{profileModalDoctor.isActive ? 'Active' : 'Inactive'}</span></div>
                <div className="profile-row"><span className="profile-label">Joined:</span><span className="profile-value">{formatDate(profileModalDoctor.createdDate)}</span></div>
                <div className="profile-row"><span className="profile-label">Last Login:</span><span className="profile-value">{profileModalDoctor.lastLogin ? formatDate(profileModalDoctor.lastLogin) : 'Never'}</span></div>
              </div>
              <div className="profile-tokens-section">
                <div className="profile-row"><span className="profile-label">Token Balance:</span><span className="profile-value">{profileModalDoctor.balance} tokens</span></div>
                <div className="profile-row"><span className="profile-label">AI Token Balance:</span><span className="profile-value">{profileModalDoctor.aiTokenBalance} tokens</span></div>
                <div className="profile-row"><span className="profile-label">Total Earned:</span><span className="profile-value">{profileModalDoctor.totalEarned} tokens</span></div>
                <div className="profile-row"><span className="profile-label">Total Spent:</span><span className="profile-value">{profileModalDoctor.totalSpent} tokens</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCloseProfile}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Balance Modal ── */}
      {balanceModalDoctor && (
        <div className="modal-overlay" onClick={handleCloseBalance} role="dialog" aria-modal="true" aria-label={`Manage balance for ${balanceModalDoctor.name}`}>
          <div className="modal-content balance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Manage Balance — {balanceModalDoctor.name}</h2>
              <button className="modal-close" onClick={handleCloseBalance} aria-label="Close balance dialog">&times;</button>
            </div>
            <div className="modal-body">
              <div className="balance-current">
                Current balance: <strong>{balanceModalDoctor.balance}</strong> tokens
                &nbsp;|&nbsp; AI tokens: <strong>{balanceModalDoctor.aiTokenBalance}</strong>
              </div>

              {/* Token Type Selector */}
              <div className="form-group">
                <label className="form-label">Token Type</label>
                <div className="token-type-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${balanceTokenType === 'tokens' ? 'toggle-btn--active' : ''}`}
                    onClick={() => setBalanceTokenType('tokens')}
                  >
                    Tokens
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${balanceTokenType === 'ai_tokens' ? 'toggle-btn--active' : ''}`}
                    onClick={() => setBalanceTokenType('ai_tokens')}
                  >
                    AI Tokens
                  </button>
                </div>
              </div>

              {balanceSubmitSuccess && (
                <div className="alert alert-success" role="status">{balanceSubmitSuccess}</div>
              )}
              {balanceSubmitError && (
                <div className="alert alert-error" role="alert">{balanceSubmitError}</div>
              )}
              <div className="form-group">
                <label htmlFor="balance-amount">Amount</label>
                <input
                  id="balance-amount"
                  type="number"
                  min="1"
                  className="form-input"
                  placeholder="Number of tokens"
                  value={balanceAmount}
                  onChange={(e) => setBalanceAmount(e.target.value)}
                  disabled={isSubmittingBalance}
                />
              </div>
              <div className="form-group">
                <label htmlFor="balance-description">Description</label>
                <input
                  id="balance-description"
                  type="text"
                  className="form-input"
                  placeholder="Reason for adding tokens"
                  value={balanceDescription}
                  onChange={(e) => setBalanceDescription(e.target.value)}
                  disabled={isSubmittingBalance}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseBalance} disabled={isSubmittingBalance}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmitBalance} disabled={isSubmittingBalance}>
                {isSubmittingBalance ? 'Adding...' : `Add ${balanceTokenType === 'ai_tokens' ? 'AI ' : ''}Tokens`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Activity Modal ── */}
      {activityModalDoctor && (
        <div className="modal-overlay" onClick={handleCloseActivity} role="dialog" aria-modal="true" aria-label={`Activity for ${activityModalDoctor.name}`}>
          <div className="modal-content activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Activity — {activityModalDoctor.name}</h2>
              <button className="modal-close" onClick={handleCloseActivity} aria-label="Close activity dialog">&times;</button>
            </div>
            <div className="modal-body">
              {isLoadingActivity ? (
                <div className="loading-overlay" role="status"><span className="spinner" aria-hidden="true"></span><span>Loading activity...</span></div>
              ) : transactions.length === 0 ? (
                <div className="empty-state">
                  <p>No token activity recorded for this doctor.</p>
                </div>
              ) : (
                <>
                  <table className="activity-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(tx.createdAt)}</td>
                          <td><span className={`tx-type ${tx.transactionType}`}>{tx.transactionType === 'credit' ? 'Credit' : tx.transactionType === 'debit' ? 'Debit' : tx.transactionType}</span></td>
                          <td className={tx.transactionType === 'credit' ? 'tx-credit' : 'tx-debit'}>{tx.transactionType === 'credit' ? '+' : '-'}{tx.amount}</td>
                          <td>{tx.description || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {activityTotalPages > 1 && (
                    <div className="pagination activity-pagination">
                      <button className="pagination-btn" onClick={() => handleActivityPageChange(activityPage - 1)} disabled={activityPage <= 1}>Previous</button>
                      <span className="pagination-info">Page {activityPage} of {activityTotalPages}</span>
                      <button className="pagination-btn" onClick={() => handleActivityPageChange(activityPage + 1)} disabled={activityPage >= activityTotalPages}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCloseActivity}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete Account Modal ── */}
      {deleteModalDoctor && (
        <div className="modal-overlay" onClick={handleCloseDelete} role="dialog" aria-modal="true" aria-label={`Delete account for ${deleteModalDoctor.name}`}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-header--danger">
              <h2 className="modal-title">Delete Account — {deleteModalDoctor.name}</h2>
              <button className="modal-close" onClick={handleCloseDelete} aria-label="Close delete dialog">&times;</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-error" role="alert">
                <strong>Warning:</strong> This action is permanent and cannot be undone.
                All data associated with <strong>{deleteModalDoctor.name}</strong> (@{deleteModalDoctor.username}) will be permanently deleted, including projects, patients, tokens, and activity history.
              </div>
              {deleteError && (
                <div className="alert alert-error" role="alert">{deleteError}</div>
              )}
              <div className="form-group">
                <label htmlFor="delete-password">Enter your admin password to confirm:</label>
                <input
                  id="delete-password"
                  type="password"
                  className="form-input"
                  placeholder="Admin password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  disabled={isDeleting}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseDelete} disabled={isDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteAccount} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete Account Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

